import Notification from 'components/Notification';
import { IMainLayoutDispatchProps, IMainLayoutStateProps } from 'containers/MainLayout/types';
import { onMessageListener } from 'firebase';
import { isSupported } from 'firebase/messaging';
import { withRouter, RouteComponentProps, RouteProps } from 'react-router-dom';
import React, { FC, lazy, useEffect, useMemo, Suspense, useRef, ReactElement } from 'react';
import styled from 'styled-components';
import media from 'utils/mediaStyle';
import { ErrorBoundary } from './CategoryNav/ErrorBoundary';
import TopNav from './MainNav/TopNav';
import FavoriteCategoryNav from 'containers/FavoriteCategoryNav';
import { Viewport } from 'components/ViewportProvider';
import SideNavNewDesign from 'components/MainNav/SideNavNewDesign';
import TabPages from './TabPages';
import BreadCrumb from './BreadCrumb';
import ChatSupport from 'components/ChatSupport';
import { useIntersection } from '../hooks';
import LoadingIndicator from 'components/LoadingIndicator';
const Content = styled.section<any>`
  margin-left: 0;
  position: relative;
  flex-shrink: 1;
  flex-grow: 1;
  margin-bottom: 16px;
  ${media.md`
    padding: 0 10pt;
  `};
  ${media.lg`
    overflow-x: hidden;
  `};
  padding-top: 0;
  transition: margin-left 0.3s ease-in-out;
  background: #f9fafc;
  min-height: calc(100vh - 160pt);
  ${({ isHomePage }) =>
    isHomePage &&
    `
  @media (max-width: 576px) {
    margin-bottom: 0px;
  }
  `}
  ${({ addMarginTop }) =>
    addMarginTop &&
    `
  margin-top: 76pt;
`}
`;

const MainContent = styled.div`
  max-width: 1200px;
  margin: auto;
  position: relative;
  display: flex;
  @media (max-width: 1024px) {
    display: block;
  }
  ${({ isHomePage }) =>
    isHomePage &&
    `
  @media (max-width: 767px) {
    &:before {
      content: '';
      position: fixed;
      width: 100%;
      height: 400px;
      top: 0;
      left: 0;
      background: rgb(76, 175, 80);
      z-index: -1;
    }
  }
  `}
`;

const FooterWrapper = styled.div`
  background: #ffffff;
  padding: 0 10pt;
  @media (max-width: 1024px) {
    border-bottom: 72px solid transparent;
  }
  @media (max-width: 992px) {
    padding: 0;
  }
`;

type Props = IMainLayoutStateProps & IMainLayoutDispatchProps & RouteProps & RouteComponentProps;

const pathAbleDisplaySideNav = [
  '/orders',
  '/orders-pending-approval',
  '/pending-orders-history',
  '/item-analysis',
  '/company-info',
  '/store-info',
  '/user-management',
  '/group-management',
  '/overview',
  '/account-info',
  '/account-mobile',
  '/user-details',
  '/groups',
];
const FooterComponent = lazy(() => import('components/Footer'));
const MainLayout: FC<Props> = (props) => {
  const {
    mainLayout: { buyer, store, sideNav, user, cart, notification, categories, hasUnreadNotification },
    children,
    setLang,
    signout,
    toggleSideNav,
    isPenddingApproval,
    countPendingOrder,
    registerNotificationToken,
    location,
  } = props;
  const FooterRef = useRef<ReactElement>(null);
  const isVisible = useIntersection(FooterRef, '5px');
  const userPermissions = useMemo(() => {
    const permissions = (user.permissions || []).map((permission) => permission.action);
    (user.userGroups || []).forEach((group) => {
      group.permissions.forEach((permission) => {
        if (permissions.indexOf(permission.action) === -1) {
          permissions.push(permission.action);
        }
      });
    });
    return permissions;
  }, [user.permissions, user.userGroups]);
  useEffect(() => {
    (async () => {
      if (await isSupported()) {
        onMessageListener(buyer?.id, store?.id);
      }
    })();
  }, []);

  return (
    <ErrorBoundary>
      <ErrorBoundary>
        <TopNav
          cart={cart}
          user={user}
          store={store}
          permissions={userPermissions}
          onToggle={toggleSideNav}
          onSignout={signout}
          onSetLang={setLang}
          opened={sideNav}
          categories={categories}
          hasUnreadNotification={hasUnreadNotification}
        />
      </ErrorBoundary>
      {location.pathname !== '/' && location.pathname !== '/account-mobile' && <BreadCrumb />}
      <Viewport.Consumer>
        {({ width }) => (
          <MainContent isHomePage={location.pathname === '/'}>
            {location.pathname === '/favorite' && width > 1024 && (
              <ErrorBoundary>
                <FavoriteCategoryNav />
              </ErrorBoundary>
            )}
            {pathAbleDisplaySideNav.includes(
              location.pathname.lastIndexOf('/') === 0
                ? location.pathname
                : location.pathname.substring(0, location.pathname.lastIndexOf('/')),
            ) &&
              width > 1024 && (
                <ErrorBoundary>
                  <SideNavNewDesign
                    opened={sideNav}
                    permissions={userPermissions}
                    store={store}
                    user={user}
                    onToggle={toggleSideNav}
                    isPenddingApproval={isPenddingApproval}
                    countPendingOrder={countPendingOrder}
                  />
                </ErrorBoundary>
              )}
            <Content
              addMarginTop={location.pathname === '/' || location.pathname === '/account-mobile'}
              isHomePage={location.pathname === '/'}
            >
              {pathAbleDisplaySideNav.includes(location.pathname) && (
                <ErrorBoundary>
                  <TabPages permissions={userPermissions} isPendingApproval={isPenddingApproval} />
                </ErrorBoundary>
              )}
              <ErrorBoundary>{children}</ErrorBoundary>
            </Content>
          </MainContent>
        )}
      </Viewport.Consumer>
      <ChatSupport />
      <FooterWrapper ref={FooterRef}>
        <Suspense fallback={<LoadingIndicator />}>{isVisible && !!categories.length && <FooterComponent />}</Suspense>
      </FooterWrapper>
      <Notification
        userId={user.id}
        notificationId={notification.id}
        registerNotificationToken={registerNotificationToken}
      />
    </ErrorBoundary>
  );
};

export default withRouter(MainLayout);
